import React, { useState, useEffect, useRef } from "react";
import {Modal, Button, Form, Dropdown, Spinner, Badge} from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { MdAutoAwesome, MdCheck, MdCheckCircle, MdRadioButtonUnchecked, MdPhone } from "react-icons/md";
import moment from "moment";
import "./AICallAnalysisModal.css";
import aiIconPng from "../../../../../public/assets/images/primer-logo-mini.png";
import { getCurrentUser } from "../../../../_helpers/commonFunctions";
import AICallAnalysisModal from "./AICallAnalysisModal";

const AILiveChatModal = ({ show, onHide, callsData, selectedRows, filters, companyId, existingSession }) => {
    const [step, setStep] = useState(0);
    const [mode, setMode] = useState("summary");
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [sessionUid, setSessionUid] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [inputText, setInputText] = useState("");
    const [isPolling, setIsPolling] = useState(false);
    const chatEndRef = useRef(null);
    const textareaRef = useRef(null);
    const [isSwitchingMode, setIsSwitchingMode] = useState(false);
    const currentUser = getCurrentUser();
    const [showAnalysisModal, setShowAnalysisModal] = useState(false);
    const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
    const [selectedCallData, setSelectedCallData] = useState({
        analysisData: null,
        row: null,
        cost: 0,
        date: null
    });

    useEffect(() => {
        if (!show) {
            setStep(0);
            setMode("summary");
            setSessionUid(null);
            setChatHistory([]);
            setInputText("");
            setIsPolling(false);
        } else {
            if (existingSession) {
                setSessionUid(existingSession.uniqueIdentifier);
                setChatHistory(existingSession.history || []);
                setMode(existingSession.mode || "summary");
                setStep(1);
            } else {
                setStep(0);
            }
        }
    }, [show, existingSession]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatHistory, isPolling]);

    useEffect(() => {
        let interval;
        if (isPolling && sessionUid) {
            interval = setInterval(() => {
                axios.get(`${process.env.REACT_APP_API_URL2}/ai/livechat/status/${sessionUid}`)
                    .then(res => {
                        if (res.data.state === "completed") {
                            setChatHistory(prev => [...prev, { role: "ai", text: res.data.reply, time: moment().format("HH:mm") }]);
                            setIsPolling(false);
                        } else if (res.data.state === "error") {
                            toast.error("Σφάλμα κατά την επικοινωνία με το AI.");
                            setIsPolling(false);
                        }
                    })
                    .catch(err => {
                        console.error("Polling Error:", err);
                        toast.error("Αποτυχία ελέγχου κατάστασης.");
                        setIsPolling(false);
                    });
            }, 2000);
        }
        return () => clearInterval(interval);
    }, [isPolling, sessionUid]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            const scrollHeight = textareaRef.current.scrollHeight;
            textareaRef.current.style.height = `${Math.min(scrollHeight, 150)}px`;
        }
    }, [inputText]);

    const handleOpenCallModal = async (callId) => {
        const rowData = callsData?.find(c => String(c.linkedid) === callId || String(c.aiUniqueIdentifier) === callId) || {};
        const fetchId = rowData.linkedid ? rowData.linkedid : callId;

        setShowAnalysisModal(true);
        setIsAnalysisLoading(true);

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL2}/ai/ai-cdrcall-result/${fetchId}`);
            if (res.data && res.data.status === "200") {
                setSelectedCallData({
                    analysisData: res.data.aiJSONResponse,
                    row: rowData,
                    cost: res.data.aiCost,
                    date: res.data.createdAt,
                });
            } else {
                toast.error("Δεν βρέθηκε η ανάλυση για αυτή την κλήση.");
                setShowAnalysisModal(false);
            }
        } catch (error) {
            console.error("Error fetching analysis for ID:", callId, error);
            toast.error("Σφάλμα κατά την ανάκτηση της ανάλυσης.");
            setShowAnalysisModal(false);
        } finally {
            setIsAnalysisLoading(false);
        }
    };

    const renderMessageWithLinks = (text) => {
        if (!text) return null;
        const idRegex = /##(.*?)##/g;
        const parts = text.split(idRegex);

        return parts.map((part, index) => {
            if (index % 2 === 1) {
                const cleanCallId = part.trim();

                let displayId = cleanCallId;
                if (cleanCallId.includes(".")) {
                    displayId = cleanCallId.substring(cleanCallId.indexOf(".") + 1);
                }

                return (
                    <Badge
                        key={index}
                        bg="primary"
                        style={{
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'normal',
                            padding: '4px 8px',
                            margin: '0 4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            verticalAlign: 'middle',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                        onClick={() => handleOpenCallModal(cleanCallId)}
                        title={`Προβολή ανάλυσης κλήσης ${cleanCallId}`}
                    >
                        <MdPhone size={12} className="mr-1" />
                        {displayId}
                    </Badge>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    const handleStartChat = async () => {
        let callsToProcess = [];
        setIsLoading(true);
        setLoadingText("Προετοιμασία δεδομένων...");

        try {
            if (selectedRows.length > 0 && !selectedRows.includes("-1")) {
                callsToProcess = callsData.filter(c => selectedRows.includes(String(c.linkedid)) && c.aiStatus === 100);
            } else {
                setLoadingText("Ανάκτηση όλων των φιλτραρισμένων κλήσεων από τον server...");
                const res = await axios.post(`${process.env.REACT_APP_API_URL2}/callcenter/fetch-cdr-viewer`, {
                    ...filters,
                    page: -1,
                    company: companyId,
                    fromPagination: false,
                    forceRefresh: false,
                    userId: getCurrentUser()._id
                });

                if (res.data.status === "200") {
                    callsToProcess = res.data.data.filter(c => c.aiStatus === 100);
                }
            }

            if (callsToProcess.length === 0) {
                setIsLoading(false);
                toast.info("Δεν βρέθηκαν κλήσεις με ολοκληρωμένη AI Ανάλυση στο επιλεγμένο εύρος.");
                return;
            }

            setLoadingText("Δημιουργία AI Cache (Αρχικοποίηση)...");

            const linkedIdsArray = callsToProcess.map(c => String(c.linkedid));

            const initResponse = await axios.post(`${process.env.REACT_APP_API_URL2}/ai/livechat/init`, {
                mode: mode,
                linkedIds: linkedIdsArray,
                clientIdentifier: `Μαζική Ανάλυση (${callsToProcess.length} κλήσεις)`,
                user: `${currentUser.firstName} ${currentUser.lastName} (${currentUser.userName})`,
            });

            if (initResponse.data.status === "200") {
                setSessionUid(initResponse.data.uniqueIdentifier);
                setStep(1);

                setChatHistory([
                    {
                        role: "ai",
                        text: `Γεια σας! Τα δεδομένα από ${callsToProcess.length} κλήσεις φορτώθηκαν και είμαι έτοιμος να απαντήσω στις ερωτήσεις σας.`,
                        time: moment().format("HH:mm")
                    }
                ]);
            } else {
                toast.error(initResponse.data.message || "Αποτυχία αρχικοποίησης Chat.");
            }
        } catch (error) {
            console.error("Init Error:", error);
            const errorMessage = error?.response?.data?.message || "Σφάλμα κατά την προετοιμασία των δεδομένων.";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendMessage = () => {
        if (!inputText.trim() || isPolling) return;

        const userMsg = inputText.trim();
        setInputText("");
        setChatHistory(prev => [...prev, { role: "user", text: userMsg, time: moment().format("HH:mm") }]);
        setIsPolling(true);

        axios.post(`${process.env.REACT_APP_API_URL2}/ai/livechat/send`, {
            uniqueIdentifier: sessionUid,
            message: userMsg
        }).catch(err => {
            console.error("Send Error:", err);
            toast.error("Αποτυχία αποστολής μηνύματος.");
            setIsPolling(false);
        });
    };

    const handleModeSwitch = async (newMode) => {
        if (newMode === mode || isSwitchingMode) return;

        setIsSwitchingMode(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL2}/ai/livechat/switch-mode`, {
                uniqueIdentifier: sessionUid,
                newMode: newMode
            });

            if (res.data.status === "200") {
                setMode(newMode);
                setChatHistory(prev => [...prev, {
                    role: "system",
                    text: `[Σύστημα] Η ανάλυση άλλαξε σε: ${newMode === 'summary' ? 'Περιλήψεις' : 'Transcripts'}.`,
                    time: moment().format("HH:mm")
                }]);
                toast.success("Η λειτουργία του AI άλλαξε επιτυχώς.");
            } else {
                toast.error("Αποτυχία αλλαγής λειτουργίας.");
            }
        } catch (err) {
            console.error("Mode switch error:", err);
            toast.error("Σφάλμα δικτύου.");
        } finally {
            setIsSwitchingMode(false);
        }
    };

    return (
        <>
            <Modal show={show} onHide={onHide} centered scrollable dialogClassName="modal65PercentWidth">
                <Modal.Header closeButton className="ai-modal-header">
                    <div className="d-flex align-items-center justify-content-center w-100">
                        <div className="ai-subject-tag d-flex align-items-center m-0"
                             style={{fontSize: '0.8rem', fontWeight: 'bold'}}>
                            <MdAutoAwesome size={14} className="mr-2"/>
                            Συνομιλία με το Primer AI
                        </div>
                        {step === 1 && (
                            <div className="d-flex align-items-center ml-3">
                                {isSwitchingMode && (
                                    <Spinner animation="border" size="sm" className="mr-2 text-secondary"/>
                                )}

                                <Dropdown>
                                    <Dropdown.Toggle
                                        variant="light"
                                        size="sm"
                                        id="mode-dropdown"
                                        disabled={isSwitchingMode}
                                        className="d-flex align-items-center border shadow-sm"
                                        style={{backgroundColor: '#fff', borderRadius: '4px'}}
                                    >
                                        <span className="text-muted mr-2" style={{fontSize: '12px'}}>Λειτουργία:</span>
                                        <strong style={{fontSize: '12px'}}>
                                            {mode === 'summary' ? 'Περιλήψεις' : 'Transcripts'}
                                        </strong>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu align="left" className="shadow-sm border-0" style={{borderRadius: '4px'}}>
                                        <Dropdown.Item
                                            onClick={() => handleModeSwitch("summary")}
                                            className={`mode-dropdown-item d-flex justify-content-between align-items-center py-2 ${mode === 'summary' ? 'active-mode' : ''}`}
                                            style={{ fontSize: '14px' }}
                                        >
                                            <span className={mode === 'summary' ? 'font-weight-bold text-primary' : 'text-dark'}>
                                                Περιλήψεις
                                            </span>
                                            {mode === 'summary' && <MdCheck size={18} className="text-primary ml-3" />}
                                        </Dropdown.Item>

                                        <Dropdown.Item
                                            onClick={() => handleModeSwitch("transcript")}
                                            className={`mode-dropdown-item d-flex justify-content-between align-items-center py-2 ${mode === 'transcript' ? 'active-mode' : ''}`}
                                            style={{ fontSize: '14px' }}
                                        >
                                            <span className={mode === 'transcript' ? 'font-weight-bold text-primary' : 'text-dark'}>
                                                Transcripts
                                            </span>
                                            {mode === 'transcript' && <MdCheck size={18} className="text-primary ml-3" />}
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        )}
                    </div>
                </Modal.Header>

                <Modal.Body className="ai-modal-body" style={{ display: "flex", flexDirection: "column", minHeight: "500px" }}>
                    {step === 0 && (
                        <div className="d-flex flex-column justify-content-center align-items-center h-100" style={{ flex: 1 }}>
                            {isLoading ? (
                                <div className="ai-loading-container mt-5 mb-5">
                                    <div className="typing-dots"><span></span><span></span><span></span></div>
                                    <div className="ai-loader-text mt-3">{loadingText}</div>
                                    <p className="text-muted text-center" style={{ fontSize: "14px" }}>Αυτή η διαδικασία μπορεί να διαρκέσει λίγα δευτερόλεπτα.</p>
                                </div>
                            ) : (
                                <div className="mx-auto" style={{ maxWidth: '650px' }}>
                                    <h4 className="text-center mb-4" style={{ color: '#2c3e50', fontWeight: '600' }}>
                                        Επιλέξτε τρόπο ανάλυσης
                                    </h4>

                                    <div className="d-flex flex-column">
                                        <div
                                            className="p-4 mb-3 border rounded shadow-sm d-flex align-items-center"
                                            onClick={() => setMode("summary")}
                                            style={{
                                                cursor: "pointer",
                                                transition: "all 0.2s ease-in-out",
                                                backgroundColor: mode === "summary" ? "#f4f9fa" : "#ffffff",
                                                borderColor: mode === "summary" ? "#76aeb7" : "#e9ecef",
                                                borderWidth: mode === "summary" ? "2px" : "1px",
                                                transform: mode === "summary" ? "scale(1.02)" : "scale(1)"
                                            }}
                                        >
                                            <div className="mr-3">
                                                {mode === "summary"
                                                    ? <MdCheckCircle size={28} color="#76aeb7" />
                                                    : <MdRadioButtonUnchecked size={28} color="#ced4da" />
                                                }
                                            </div>
                                            <div>
                                                <div style={{ fontSize: "16px", fontWeight: "600", color: mode === "summary" ? "#0f515c" : "#343a40" }}>
                                                    Συνομιλία με βάση τις Περιλήψεις (Summaries)
                                                </div>
                                                <div className="text-muted mt-1" style={{ fontSize: "13.5px" }}>
                                                    Γρήγορη ανάλυση με βάση τη σύνοψη και τα σημαντικότερα σημεία των κλήσεων.
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="p-4 border rounded shadow-sm d-flex align-items-center"
                                            onClick={() => setMode("transcript")}
                                            style={{
                                                cursor: "pointer",
                                                transition: "all 0.2s ease-in-out",
                                                backgroundColor: mode === "transcript" ? "#f4f9fa" : "#ffffff",
                                                borderColor: mode === "transcript" ? "#76aeb7" : "#e9ecef",
                                                borderWidth: mode === "transcript" ? "2px" : "1px",
                                                transform: mode === "transcript" ? "scale(1.02)" : "scale(1)"
                                            }}
                                        >
                                            <div className="mr-3">
                                                {mode === "transcript"
                                                    ? <MdCheckCircle size={28} color="#76aeb7" />
                                                    : <MdRadioButtonUnchecked size={28} color="#ced4da" />
                                                }
                                            </div>
                                            <div>
                                                <div style={{ fontSize: "16px", fontWeight: "600", color: mode === "transcript" ? "#0f515c" : "#343a40" }}>
                                                    Συνομιλία με βάση τα Πλήρη Κείμενα (Transcripts)
                                                </div>
                                                <div className="text-muted mt-1" style={{ fontSize: "13.5px" }}>
                                                    Αναλυτική αναζήτηση λέξη προς λέξη πάνω στην πλήρη απομαγνητοφώνηση.
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-center mt-5">
                                        <Button
                                            variant="primary"
                                            onClick={handleStartChat}
                                        >
                                            Έναρξη Συνομιλίας
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 1 && (
                        <div className="d-flex flex-column h-100" style={{ flex: 1 }}>
                            <div className="chat-container pt-3 px-3" style={{ flex: 1, overflowY: "auto", paddingBottom: "20px", paddingRight: "15px" }}>
                                {chatHistory.map((msg, idx) => {
                                    const isUser = msg.role === "user";
                                    const typeClass = isUser ? 'agent' : 'customer';
                                    const hasPrev = idx > 0 && chatHistory[idx - 1].role === msg.role;
                                    const hasNext = idx < chatHistory.length - 1 && chatHistory[idx + 1].role === msg.role;

                                    let dynamicBorderRadius = "18px";
                                    if (typeClass === 'agent') {
                                        if (!hasPrev && !hasNext) dynamicBorderRadius = "18px 18px 4px 18px";
                                        else if (!hasPrev && hasNext) dynamicBorderRadius = "18px 18px 4px 18px";
                                        else if (hasPrev && hasNext) dynamicBorderRadius = "18px 4px 4px 18px";
                                        else if (hasPrev && !hasNext) dynamicBorderRadius = "18px 4px 18px 18px";
                                    } else {
                                        if (!hasPrev && !hasNext) dynamicBorderRadius = "18px 18px 18px 4px";
                                        else if (!hasPrev && hasNext) dynamicBorderRadius = "18px 18px 18px 4px";
                                        else if (hasPrev && hasNext) dynamicBorderRadius = "2px 18px 18px 4px";
                                        else if (hasPrev && !hasNext) dynamicBorderRadius = "2px 18px 18px 18px";
                                    }

                                    return (
                                        <div
                                            key={idx}
                                            className={`chat-bubble-wrapper ${typeClass}`}
                                            style={{ marginTop: hasPrev ? '1px' : '16px', marginBottom: '0px' }}
                                        >
                                            {!hasPrev && (
                                                <span className="chat-role-label d-flex align-items-center">
                                                    {isUser ? (
                                                        `${currentUser.firstName} ${currentUser.lastName}`
                                                    ) : (
                                                        <>
                                                            <img
                                                                src={aiIconPng}
                                                                alt="Primer Logo"
                                                                className="tiny-ai-icon-png"
                                                            />
                                                            Primer AI
                                                        </>
                                                    )}
                                                </span>
                                            )}
                                            <div
                                                className={`chat-bubble ${typeClass}`}
                                                style={{
                                                    borderRadius: dynamicBorderRadius,
                                                    position: 'relative',
                                                    paddingBottom: '20px',
                                                    whiteSpace: 'pre-wrap'
                                                }}
                                            >
                                                {/* Εδώ εφαρμόζεται η συνάρτηση για τα clickable links */}
                                                {renderMessageWithLinks(msg.text)}
                                                <span
                                                    className="chat-time-inline"
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: '4px',
                                                        right: '10px',
                                                        fontSize: '11px',
                                                        opacity: 0.65,
                                                        userSelect: 'none'
                                                    }}
                                                >
                                                    {msg.time}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}

                                {isPolling && (
                                    <div className="chat-bubble-wrapper customer" style={{ marginTop: '16px' }}>
                                        <span className="chat-role-label d-flex align-items-center">
                                            <img
                                                src={aiIconPng}
                                                alt="Primer Logo"
                                                className="tiny-ai-icon-png"
                                            />
                                            Primer AI
                                        </span>
                                        <div className="chat-bubble customer" style={{ borderRadius: "18px 18px 18px 4px", padding: "12px 20px" }}>
                                            <div className="typing-dots" style={{ margin: 0 }}><span></span><span></span><span></span></div>
                                        </div>
                                    </div>
                                )}
                                <div ref={chatEndRef} />
                            </div>
                        </div>
                    )}
                </Modal.Body>

                {step === 1 && (
                    <Modal.Footer className="ai-modal-footer d-flex flex-column align-items-stretch" style={{ gap: "10px" }}>
                        <div className="chat-input-container d-flex align-items-end w-100">
                            <Form.Control
                                as="textarea"
                                ref={textareaRef}
                                rows={1}
                                placeholder="Ρωτήστε το Primer AI..."
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                disabled={isPolling}
                                style={{
                                    resize: "none",
                                    borderRadius: "4px",
                                    overflowY: "auto",
                                    minHeight: "46px"
                                }}
                            />
                            <Button
                                variant="primary"
                                className="ml-2 d-flex align-items-center justify-content-center"
                                style={{ width: "50px", borderRadius: "4px" }}
                                onClick={handleSendMessage}
                                disabled={isPolling || !inputText.trim()}
                            >
                                <i className="fa fa-send"></i>
                            </Button>
                        </div>
                        <div className="text-muted text-right w-100" style={{ fontSize: "11px" }}>
                            Πατήστε Enter για αποστολή. Shift + Enter για αλλαγή γραμμής.
                        </div>
                    </Modal.Footer>
                )}
            </Modal>

            <AICallAnalysisModal
                show={showAnalysisModal}
                onHide={() => setShowAnalysisModal(false)}
                data={selectedCallData.analysisData}
                analysisDate={selectedCallData.date}
                cost={selectedCallData.cost}
                isLoading={isAnalysisLoading}
                row={selectedCallData.row}
            />
        </>
    );
};

export default AILiveChatModal;